import { create } from 'zustand';
import { systemModuleService } from '../services';
import type { FeatureFlagMetadataMap, SystemModuleDto, UpdateModuleRequest } from '../types';

/**
 * Maps permission code prefixes to the module code that owns them.
 * Used to determine which module a permission belongs to for enforcement.
 */
export const PERMISSION_PREFIX_TO_MODULE: Record<string, string> = {
  'mod.': 'MODM',
  'auth.': 'AUTM',
  'user.': 'USEM',
  'issuance.': 'ISSE',
  'receiving.': 'RECV',
};

interface SystemModuleState {
  /**
   * The ONE module list, written by both fetchAvailability() and fetchModules() — the two endpoints
   * return the same SystemModuleDto shape. Deliberately not split in two: ModuleGuard reads this field,
   * so a second array would leave it stale on whichever page didn't populate it.
   */
  modules: SystemModuleDto[];
  featureFlagMetadata: FeatureFlagMetadataMap;
  /** Admin fetch (Module Management page). */
  isLoading: boolean;
  error: string | null;
  /** App-shell availability fetch — kept separate so an app-load failure can't paint the admin page. */
  isAvailabilityLoading: boolean;
  availabilityError: string | null;
  isSaving: boolean;

  /** App shell, every authenticated user. Permission-free endpoint; feeds ModuleGuard. */
  fetchAvailability: () => Promise<void>;
  /** Module Management page only — admin endpoint. */
  fetchModules: () => Promise<void>;
  fetchFeatureFlagMetadata: () => Promise<void>;
  updateModule: (id: number, request: UpdateModuleRequest) => Promise<void>;
  /** Returns true if the module is enabled and not blocking the given user (isAdmin for admin bypass check). */
  isModuleAccessible: (moduleCode: string, isAdmin: boolean) => boolean;
  /** Returns the module code for a given permission code, or null if not mapped. */
  getModuleCodeForPermission: (permission: string) => string | null;
}

export const useSystemModuleStore = create<SystemModuleState>()((set, get) => ({
  modules: [],
  featureFlagMetadata: {},
  isLoading: false,
  error: null,
  isAvailabilityLoading: false,
  availabilityError: null,
  isSaving: false,

  fetchAvailability: async () => {
    set({ isAvailabilityLoading: true, availabilityError: null });
    try {
      const modules = await systemModuleService.getAvailability();
      set({ modules, isAvailabilityLoading: false });
    } catch (err) {
      // Fail open — see the note on isModuleAccessible. The request is marked `background`, so this
      // failure is silent by design; the message is kept here for any UI that wants to show it inline.
      set({
        isAvailabilityLoading: false,
        availabilityError: err instanceof Error ? err.message : 'Failed to load module availability',
      });
    }
  },

  fetchModules: async () => {
    set({ isLoading: true, error: null });
    try {
      const modules = await systemModuleService.getAll();
      set({ modules, isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to load modules' });
    }
  },

  // Static UI metadata for the flag editors — only the Module Management page needs it, and the endpoint
  // is admin-gated. It used to be fetched alongside the module list on every app load, which is half of
  // why every non-admin got a 403 toast (86d3va61r).
  fetchFeatureFlagMetadata: async () => {
    try {
      const metadata = await systemModuleService.getFeatureFlagMetadata();
      set({ featureFlagMetadata: metadata });
    } catch {
      // Non-fatal: with no metadata the page simply renders no flag editors. The enable / maintenance /
      // admin-override controls and the maintenance message all still work.
      set({ featureFlagMetadata: {} });
    }
  },

  updateModule: async (id, request) => {
    set({ isSaving: true, error: null });
    try {
      const updated = await systemModuleService.update(id, request);
      set((state) => ({
        modules: state.modules.map((m) => (m.id === id ? updated : m)),
        isSaving: false,
      }));
    } catch (err) {
      set({ isSaving: false, error: err instanceof Error ? err.message : 'Failed to update module' });
      throw err;
    }
  },

  isModuleAccessible: (moduleCode, isAdmin) => {
    const { modules } = get();
    const module = modules.find((m) => m.moduleCode === moduleCode);
    // FAIL OPEN — a deliberate decision (86d3va61r), not an accident of load order. If availability
    // couldn't be fetched we let the user through: a transient API blip must never lock the whole company
    // out of every guarded screen, and blocking buys no safety that ModuleEnforcementMiddleware doesn't
    // already provide server-side. Note this used to be permanent for non-admins — the list was fetched
    // from an admin-only endpoint, so the guard was inert for exactly the users it exists to stop.
    if (!module) return true;
    // Hard block: disabled for everyone
    if (!module.isEnabled) return false;
    // Soft block: under maintenance — admins bypass if allowed
    if (module.isUnderMaintenance) {
      return isAdmin && module.allowAdminOverride;
    }
    return true;
  },

  getModuleCodeForPermission: (permission) => {
    for (const [prefix, code] of Object.entries(PERMISSION_PREFIX_TO_MODULE)) {
      if (permission.startsWith(prefix)) return code;
    }
    return null;
  },
}));
